import { useState } from "react"
import { IconButton } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { Dropdown } from "@nova/component-dropdown"
import { Tooltip } from "panels/content/top-bar/TODO-put-in-package/Tooltip"
import { config } from "@nova/configuration/browser"
import { TimeFormat } from "@nova/format"
import { getFractionalIndexForElapsed } from "tools/hooks/useTelemetryPlayback"

const PLAYBACK_SPEEDS = [1, 2, 5, 10]

const formatTime = (ms: number): string => {
    if (!Number.isFinite(ms) || ms < 0) return "00:00"
    const totalSeconds = Math.floor(ms / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
}

const formatClockTime = (epochMs: number, timeFormat: TimeFormat): string => {
    const date = new Date(epochMs)
    const hours = date.getHours()
    const mm = String(date.getMinutes()).padStart(2, "0")
    const ss = String(date.getSeconds()).padStart(2, "0")
    if (timeFormat === TimeFormat.TwelveHour) {
        const amPm = hours >= 12 ? "pm" : "am"
        const displayHours = hours % 12 || 12
        return `${displayHours}:${mm}:${ss}${amPm}`
    }
    return `${String(hours).padStart(2, "0")}:${mm}:${ss}`
}

interface PlaybackControlsProps {
    playbackIndex: number
    isPlaying: boolean
    timestamps: number[]
    onPlayPause: () => void
    onSeek: (idx: number) => void
    onSeekStart?: () => void
    onSeekEnd?: () => void
    speed?: number
    onSpeedChange?: (speed: number) => void
    disablePlayback?: boolean
    disabledTooltip?: string
}

export const PlaybackControls = ({
    playbackIndex,
    isPlaying,
    timestamps,
    onPlayPause,
    onSeek,
    onSeekStart,
    onSeekEnd,
    speed,
    onSpeedChange,
    disablePlayback = false,
    disabledTooltip = "Playback unavailable. Telemetry data is missing.",
}: PlaybackControlsProps) => {
    const [showRealTime, setShowRealTime] = useState(false)
    const { organizationSettingTimeFormat } = config.use("organizationSettingTimeFormat")
    const lo = Math.floor(playbackIndex)
    const hi = Math.min(lo + 1, timestamps.length - 1)
    const t = playbackIndex - lo
    const currentMs =
        timestamps.length > 0
            ? timestamps[lo] + t * (timestamps[hi] - timestamps[lo]) - timestamps[0]
            : 0
    const totalMs = timestamps.length > 1 ? timestamps[timestamps.length - 1] - timestamps[0] : 0
    const progress = totalMs > 0 ? currentMs / totalMs : 0

    return (
        <div className="flex flex-col gap-2">
            <div className="relative h-1 w-full bg-gray-600 rounded-full">
                <div
                    className="absolute inset-y-0 left-0 bg-gray-300 rounded-full"
                    style={{ width: `${progress * 100}%` }}
                />
                <input
                    type="range"
                    min={0}
                    max={totalMs}
                    value={currentMs}
                    onChange={(e) =>
                        onSeek(
                            getFractionalIndexForElapsed(Number(e.currentTarget.value), timestamps)
                        )
                    }
                    onPointerDown={(e) => {
                        e.stopPropagation()
                        onSeekStart?.()
                    }}
                    onPointerUp={() => onSeekEnd?.()}
                    onPointerCancel={() => onSeekEnd?.()}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
            </div>
            <div className="w-full flex items-center gap-2">
                <div className="relative group/tooltip flex items-center">
                    <IconButton onClick={onPlayPause} disabled={disablePlayback}>
                        <Icons.SmallIcon
                            Icon={isPlaying ? Icons.Pause : Icons.Play}
                            className="text-gray-300 hover:text-gray-100"
                        />
                    </IconButton>
                    {disablePlayback && (
                        <Tooltip>
                            <div className="flex flex-col items-center">{disabledTooltip}</div>
                        </Tooltip>
                    )}
                </div>
                {speed !== undefined && onSpeedChange && (
                    <Dropdown>
                        <Dropdown.Trigger>
                            <div className="cursor-pointer text-gray-300 hover:text-gray-100">
                                {speed}x
                            </div>
                        </Dropdown.Trigger>
                        <Dropdown.Menu position="bottom-right">
                            {PLAYBACK_SPEEDS.map((option) => (
                                <Dropdown.Item
                                    key={option}
                                    onClick={() => onSpeedChange(option)}
                                    className="px-4 py-2.5 text-sm text-white hover:bg-gray-500 transition-colors cursor-pointer"
                                >
                                    {option}x
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                )}
                <span
                    className="text-gray-300 text-sm cursor-pointer hover:text-gray-100"
                    onClick={() => {
                        setShowRealTime((v) => !v)
                    }}
                >
                    {disablePlayback
                        ? "--:-- / --:--"
                        : showRealTime && timestamps.length > 0
                          ? formatClockTime(
                                timestamps[0] + currentMs,
                                organizationSettingTimeFormat as TimeFormat
                            )
                          : `${formatTime(currentMs)} / ${formatTime(totalMs)}`}
                </span>
            </div>
        </div>
    )
}
